<template>
  <div class="employee-home cabinet-page" :class="{ 'is-loading': loading }">
    <app-page-loader v-if="loading" />
    <cabinet-state v-else-if="loadError" type="error" :title="$t('overview.unavailable')" :message="$t('overview.loadError')" @retry="loadOverview" />
    <template v-else>
      <header class="employee-home__welcome">
        <span>{{ greeting }},</span>
        <h1>{{ employee.display_name || employee.full_name || $t('overview.employee') }}</h1>
        <p v-if="employee.employment_since_year">{{ $t('overview.since', { year: employee.employment_since_year }) }}</p>
      </header>

      <section class="salary-card" :class="{ 'salary-card--empty': !salary }">
        <div class="salary-card__top">
          <span v-if="salary">{{ $t('overview.salary') }}&nbsp;&nbsp;·&nbsp;&nbsp;{{ formatPeriod(salary.period) }}</span>
          <span v-else>{{ $t('overview.salaryEstimate') }}</span>
          <button v-if="salary" type="button" :aria-label="salaryVisible ? $t('overview.hideSalary') : $t('overview.showSalary')" @click="salaryVisible = !salaryVisible">
            <img src="@/assets/img/employee/eye.svg" alt="">
          </button>
        </div>
        <template v-if="salary">
          <div class="salary-card__amount">
            <small>TZS</small>
            <strong v-if="salaryVisible">{{ formatAmount(salary.amount) }}</strong>
            <span v-else class="salary-card__masked" role="img" :aria-label="$t('overview.salaryHidden')">
              <i v-for="dot in 7" :key="dot"></i>
            </span>
          </div>
          <p class="salary-card__note">{{ $t('overview.debtNote') }}</p>
          <div class="salary-card__payday"><span>{{ $t('overview.netOf') }}</span><strong>{{ $t('overview.payday', { date: formatShortDate(salary.payday) }) }}</strong></div>
        </template>
        <div v-else class="salary-card__empty"><strong>{{ $t('overview.salaryMissing') }}</strong><p>{{ $t('overview.salaryMissingHelp') }}</p></div>
      </section>

      <section class="employee-actions">
        <div class="employee-section-heading"><span>{{ $t('overview.service') }}</span></div>
        <div class="employee-actions__grid">
          <router-link v-for="action in actions" :key="action.labelKey" :to="action.to" class="employee-action-card">
            <span class="employee-action-card__art" :style="artStyle(action)"><img :src="action.image" alt=""></span><strong>{{ $t(action.labelKey) }}</strong><small>{{ $t(action.noteKey) }}</small>
          </router-link>
        </div>
      </section>
      <p class="employee-home__promise">{{ $t('overview.promise') }}<em>{{ $t('overview.promiseTime') }}</em></p>
    </template>
  </div>
</template>

<script>
import { getPersonalCabinetOverview } from '@/http'
import { formatLocalizedDate, localeTag } from '@/i18n'
import CabinetState from './CabinetState'

export default {
  name: 'PersonalCabinetOverview',
  components: { CabinetState },
  data() {
    return {
      loading: true,
      loadError: false,
      overview: {},
      salaryVisible: true,
      actions: [
        { labelKey: 'overview.requestAdvance', noteKey: 'overview.salaryAdvance', image: require('@/assets/img/employee/advance.png'), artWidth: 100, artHeight: 100, to: { name: 'personal-cabinet-advances' } },
        { labelKey: 'overview.requestLoan', noteKey: 'overview.staffProgramme', image: require('@/assets/img/employee/loan.png'), artWidth: 100, artHeight: 87, to: { name: 'personal-cabinet-loans' } },
        { labelKey: 'overview.reportSick', noteKey: 'overview.leaveAbsence', image: require('@/assets/img/employee/sick.png'), artWidth: 100, artHeight: 70, to: { name: 'personal-cabinet-medical-reports', query: { type: 'sick_leave' } } },
        { labelKey: 'overview.requestVacation', noteKey: 'overview.annualLeave', image: require('@/assets/img/employee/vacation.png'), artWidth: 100, artHeight: 71, to: { name: 'personal-cabinet-leave', query: { type: 'paid_leave' } } },
        { labelKey: 'overview.maternity', noteKey: 'overview.parental', image: require('@/assets/img/employee/parental.png'), artWidth: 100, artHeight: 100, to: { name: 'personal-cabinet-medical-reports', query: { type: 'parental' } } },
        { labelKey: 'overview.unpaid', noteKey: 'overview.personalTime', image: require('@/assets/img/employee/unpaid.png'), artWidth: 100, artHeight: 69, to: { name: 'personal-cabinet-leave', query: { type: 'unpaid_leave' } } },
        { labelKey: 'overview.reportMisconduct', noteKey: 'overview.confidence', image: require('@/assets/img/employee/misconduct.png'), artWidth: 100, artHeight: 69, to: { name: 'personal-cabinet-misconduct' } },
        { labelKey: 'overview.suggestIdea', noteKey: 'overview.founders', image: require('@/assets/img/employee/idea.png'), artWidth: 100, artHeight: 73, to: { name: 'personal-cabinet-idea' } },
        { labelKey: 'overview.myHistory', noteKey: 'overview.pastRequests', image: require('@/assets/img/employee/history.png'), artWidth: 100, artHeight: 66, to: { name: 'personal-cabinet-history' } },
      ],
    }
  },
  computed: {
    employee() { return this.overview.employee || {} },
    salary() { return this.overview.current_salary || null },
    greeting() {
      const hour = new Date().getHours()
      if (hour < 12) return this.$t('overview.morning')
      if (hour < 18) return this.$t('overview.afternoon')
      return this.$t('overview.evening')
    },
  },
  created() { this.loadOverview() },
  methods: {
    artStyle(action) {
      return {
        '--employee-art-width': `${action.artWidth}px`,
        '--employee-art-height': `${action.artHeight}px`,
      }
    },
    loadOverview() {
      this.loading = true
      this.loadError = false
      return getPersonalCabinetOverview()
        .then(({ data }) => { this.overview = data })
        .catch(() => { this.loadError = true })
        .finally(() => { this.loading = false })
    },
    formatAmount(value) { return new Intl.NumberFormat(localeTag(), { maximumFractionDigits: 0 }).format(value || 0) },
    formatShortDate(value) { return formatLocalizedDate(value, { month: 'short', day: '2-digit' }) },
    formatPeriod(value) { return value ? formatLocalizedDate(value, { month: 'long', year: 'numeric' }) : '' },
  },
}
</script>

<style lang="sass" scoped>
.employee-home__welcome
  margin: 0 0 39px
  > span
    color: #49463d
    font-size: 20px
    font-weight: 500
    line-height: 21px
  h1
    max-width: 380px
    margin-top: -1px
    color: #b89b6c
    font-family: 'Cormorant Upright', 'Cormorant_Upright', Georgia, serif
    font-size: 43px
    font-weight: 600
    line-height: 44px
    letter-spacing: -.86px
  p
    margin-top: 13px
    color: #8e806a
    font-size: 11px
    font-weight: 600
    letter-spacing: .55px
    line-height: 18px
    text-transform: uppercase
.salary-card
  position: relative
  overflow: hidden
  height: 225px
  padding: 20px 25px
  border-radius: 20px
  background: linear-gradient(112deg, #413e30 16%, #5b5443 50%, #544c3c 78%)
  color: #fff
  box-shadow: 0 1px 5px rgba(159, 167, 177, .24)
.salary-card__top, .salary-card__payday
  display: flex
  align-items: flex-start
  justify-content: space-between
  gap: 5px
.salary-card__top
  span
    color: #dad2ba
    font-size: 12px
    font-weight: 600
    letter-spacing: 1.32px
    line-height: 18px
    text-transform: uppercase
  button
    width: 16px
    height: 16px
    img
      display: block
      width: 16px
      height: 12px
.salary-card__amount
  display: flex
  align-items: flex-start
  gap: 7px
  margin: 39px 0 27px
  min-width: 0
  small
    margin-top: 4px
    color: #c0b79b
    font-family: Inter, sans-serif
    font-size: 16px
    font-weight: 500
    letter-spacing: .96px
  strong
    min-width: 0
    color: #f9f2e1
    font-family: Inter, sans-serif
    font-size: 36px
    font-weight: 400
    line-height: 18px
.salary-card__masked
  display: inline-flex
  flex: 0 0 auto
  align-items: center
  gap: 6px
  height: 18px
  padding-top: 5px
  i
    display: block
    width: 8px
    height: 8px
    flex: 0 0 8px
    border-radius: 50%
    background: #f9f2e1
.salary-card__note
  min-height: 42px
  padding: 8px 0
  border-top: 1px solid rgba(218, 210, 186, .35)
  border-bottom: 1px solid rgba(218, 210, 186, .35)
  color: #c0b79b
  font-size: 12px
  font-weight: 500
  letter-spacing: .12px
  line-height: 14px
.salary-card__payday
  align-items: center
  padding-top: 10px
  color: #dad2ba
  font-size: 11px
  line-height: 16px
  span
    color: #c0b79b
  strong
    font-weight: 600
    letter-spacing: 1.32px
    text-transform: uppercase
    white-space: nowrap
.salary-card--empty
  display: flex
  min-height: 174px
  align-items: center
.salary-card__empty
  position: relative
  z-index: 1
  strong
    display: block
    max-width: 260px
    font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
    font-size: 21px
    font-weight: 600
    line-height: 1.2
    letter-spacing: -.2px
  p
    max-width: 270px
    margin-top: 9px
    color: #bbb7ae
    font-size: 11px
    line-height: 1.4
.employee-actions
  margin-top: 54px
.employee-section-heading
  display: flex
  align-items: center
  justify-content: space-between
  position: relative
  margin-bottom: 18px
  text-transform: uppercase
  &::after
    position: absolute
    right: 0
    top: 8px
    width: calc(100% - 132px)
    height: 1px
    background: #ded8cc
    content: ''
  span
    color: #8e806a
    font-size: 13px
    font-weight: 600
    letter-spacing: .65px
    line-height: 18px
.employee-actions__grid
  display: grid
  grid-template-columns: repeat(2, minmax(0, 1fr))
  gap: 20px
.employee-action-card
  display: flex
  flex-direction: column
  aspect-ratio: 1
  min-width: 0
  padding: 7px 12px 9px
  border: 1px solid #ebe6dc
  border-radius: 20px
  background: #faf8f4
  color: #49463d
  box-shadow: 0 1px 5px rgba(159, 167, 177, .24)
  &:active
    transform: scale(.985)
  strong
    width: 100%
    margin-top: auto
    padding-bottom: 11px
    border-bottom: 1px solid #ded8cc
    font-size: 15px
    font-weight: 500
    line-height: 16px
    text-align: center
  small
    margin-top: 7px
    color: #8e806a
    font-size: 12px
    font-weight: 500
    letter-spacing: .36px
    line-height: 18px
    text-align: center
    text-transform: uppercase
.employee-action-card__art
  display: grid
  flex: 1 1 100px
  place-items: center
  width: 100%
  height: 100px
  min-height: 0
  overflow: hidden
  img
    display: block
    width: var(--employee-art-width)
    height: var(--employee-art-height)
    max-width: 100%
    max-height: 100%
    object-fit: contain
.employee-home__promise
  margin: 32px auto 0
  color: #8e806a
  font-size: 11px
  line-height: 18px
  text-align: center
  em
    display: block
    font-weight: 500

@media (max-width: 360px)
  .employee-home__welcome h1
    font-size: 32px

  .salary-card__amount strong
    font-size: 34px
</style>
